import { useContext, useMemo } from 'react'

import { getOfferImageId, getSavedDetailsForOffer, mapExperienceTicketSelections } from 'tripPlanner/components/Bookmark/BookmarkButton/utils'
import { useEditableTrips } from 'tripPlanner/hooks/api/trip'
import { EmptyArray } from 'lib/array/arrayUtils'
import SavePrompt from './SavePrompt'
import useNewTripNameFromOffer from 'tripPlanner/hooks/useNewTripNameFromOffer'
import useShouldShowTripPlannerSaveEntryPoints from 'tripPlanner/hooks/useShouldShowTripPlannerSaveEntryPoints'
import type { ExperienceBookmarkPayload } from 'tripPlanner/api/bookmark/types'
import type { BasicTrip } from 'tripPlanner/types/common'
import ExperienceOfferPageStateContext from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateContext'
import { useCurrentExperiencePageTicketOptions } from 'hooks/ExperiencePage/useCurrentExperiencePageTicketOptions'

interface Props {
  offer: App.ExperienceOffer
}

function SavePromptExperience({ offer }: Props) {
  const shouldShowTripPlannerSaveEntryPoints =
    useShouldShowTripPlannerSaveEntryPoints()
  const experienceOfferPageState = useContext(ExperienceOfferPageStateContext)
  const allTicketOptions = useCurrentExperiencePageTicketOptions(
    offer.id,
    experienceOfferPageState,
  )

  const { data, isSuccess: tripsFetched } = useEditableTrips({
    enabled: shouldShowTripPlannerSaveEntryPoints,
  })
  const trips = data ?? EmptyArray as Array<BasicTrip>

  const details = getSavedDetailsForOffer(trips, offer.id)
  const isSaved = details.length > 0

  const newTripName = useNewTripNameFromOffer(
    offer,
    shouldShowTripPlannerSaveEntryPoints,
  )

  const bookmarkPayload = useMemo<ExperienceBookmarkPayload>(() => ({
    type: 'experience',
    code: offer.id,
    languageId: experienceOfferPageState.language?.id,
    pickupPointId: experienceOfferPageState.pickupPoint?.id,
    redemptionLocationId: experienceOfferPageState.redemptionLocation?.id,
    startDate: experienceOfferPageState.date,
    startTime: experienceOfferPageState.time,
    tickets: mapExperienceTicketSelections(
      experienceOfferPageState,
      allTicketOptions,
    ),
  }), [allTicketOptions, experienceOfferPageState, offer.id])

  if (!shouldShowTripPlannerSaveEntryPoints || isSaved || !tripsFetched) {
    return null
  }

  return <SavePrompt
    offerId={offer.id}
    offerType={offer.productType === 'transfer' ? 'transfer' : 'experience'}
    bookmarkPayload={bookmarkPayload}
    newTripName={newTripName}
    offerImageId={getOfferImageId(offer)}
    trips={trips}
  />
}

export default SavePromptExperience
